import { useState, useEffect, useCallback } from 'react'
import {
  getCollection,
  getDocument,
  addDocument,
  updateDocument,
  deleteDocument,
  subscribeToCollection,
  subscribeToDocument,
  uploadFile,
  deleteFile,
  orderBy,
  where,
  limit,
} from '../services/firebase'

// Hook to fetch and subscribe to a collection
export function useCollection(collectionName, options = {}) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const {
    realtime = true,
    orderByField = 'createdAt',
    orderDirection = 'desc',
    limitCount = null,
    whereClause = null,
    whereClauses = null,
  } = options

  // Stable dependency for the clause list (array identity changes every render)
  const whereClausesKey = whereClauses ? JSON.stringify(whereClauses) : null

  useEffect(() => {
    setLoading(true)
    setError(null)

    const constraints = []

    const clauses = whereClauses || (whereClause ? [whereClause] : [])
    clauses.forEach((clause) => {
      constraints.push(where(clause.field, clause.operator, clause.value))
    })

    if (orderByField) {
      constraints.push(orderBy(orderByField, orderDirection))
    }

    if (limitCount) {
      constraints.push(limit(limitCount))
    }

    if (realtime) {
      const unsubscribe = subscribeToCollection(collectionName, (items) => {
        setData(items)
        setLoading(false)
      }, constraints)

      return unsubscribe
    } else {
      getCollection(collectionName, constraints)
        .then(setData)
        .catch(setError)
        .finally(() => setLoading(false))
    }
  }, [collectionName, realtime, orderByField, orderDirection, limitCount, whereClause?.field, whereClause?.operator, whereClause?.value, whereClausesKey])

  const add = useCallback(async (newData) => {
    try {
      const id = await addDocument(collectionName, newData)
      return { id, success: true }
    } catch (err) {
      setError(err)
      return { success: false, error: err }
    }
  }, [collectionName])

  const update = useCallback(async (docId, newData) => {
    try {
      await updateDocument(collectionName, docId, newData)
      return { success: true }
    } catch (err) {
      setError(err)
      return { success: false, error: err }
    }
  }, [collectionName])

  const remove = useCallback(async (docId) => {
    try {
      await deleteDocument(collectionName, docId)
      return { success: true }
    } catch (err) {
      setError(err)
      return { success: false, error: err }
    }
  }, [collectionName])

  return { data, loading, error, add, update, remove, refresh: () => setLoading(true) }
}

// Hook to fetch a single document
export function useDocument(collectionName, docId) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!docId) {
      setData(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const unsubscribe = subscribeToDocument(collectionName, docId, (doc) => {
      setData(doc)
      setLoading(false)
    })

    return unsubscribe
  }, [collectionName, docId])

  const update = useCallback(async (newData) => {
    if (!docId) return { success: false, error: 'No document ID' }
    try {
      await updateDocument(collectionName, docId, newData)
      return { success: true }
    } catch (err) {
      setError(err)
      return { success: false, error: err }
    }
  }, [collectionName, docId])

  const remove = useCallback(async () => {
    if (!docId) return { success: false, error: 'No document ID' }
    try {
      await deleteDocument(collectionName, docId)
      return { success: true }
    } catch (err) {
      setError(err)
      return { success: false, error: err }
    }
  }, [collectionName, docId])

  return { data, loading, error, update, remove }
}

// Hook for file uploads
export function useFileUpload() {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(null)

  const upload = useCallback(async (file, path) => {
    setUploading(true)
    setProgress(0)
    setError(null)

    try {
      const url = await uploadFile(file, path)
      setProgress(100)
      return { success: true, url }
    } catch (err) {
      setError(err)
      return { success: false, error: err }
    } finally {
      setUploading(false)
    }
  }, [])

  const remove = useCallback(async (path) => {
    try {
      await deleteFile(path)
      return { success: true }
    } catch (err) {
      setError(err)
      return { success: false, error: err }
    }
  }, [])

  return { upload, remove, uploading, progress, error }
}

export default useCollection
